// Medical Incharge Dashboard — sidebar layout matching the coach dashboard
// (coach.js). Check-Ins / Purchases / Medical all render in-page and reuse
// the coach role's own pieces so data and validation stay identical:
//   - Record / edit / view check-in: MedicalTracker's CheckInFormModal + ViewCheckInModal
//   - Purchases: PurchasePanel
//   - Medical: DoctorBank (doctors, hospitals, diagnostic centers)
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Stethoscope,
    ShoppingCart,
    Hospital,
    MessageCircle,
    LogOut,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    CalendarDays,
    HeartPulse,
    Users,
    AlertCircle,
    Siren,
    IndianRupee,
    Search,
    Plus,
    Eye,
    Pencil,
    Trash2,
} from 'lucide-react';
import './coach-styles.css';
import './MedicalTracker.css';
import './MedicalInchargeDashboard.css';
import { CheckInFormModal, ViewCheckInModal } from './MedicalTracker';
import PurchasePanel from './purchase/PurchasePanel';
import DoctorBank from '../../pages/medical/DoctorBank';
import {
    getUserBalagruhas,
    getMedicalConditionBasedOnBalagruha,
    deleteMedicalCheckin,
    getAssignableUsersForSchedule,
    getMyPurchaseRequests,
    getCoachDeliveries,
} from '../../api';
import { useRBAC } from '../../contexts/RBACContext';
import { useAuth } from '../../contexts/AuthContext';
import RoleSwitcher from '../RoleSwitcher';
import showToast from '../../utils/toast';
import SearchableSelect from '../common/SearchableSelect';

const MENU_CHECKINS = 'checkins';
const MENU_PURCHASES = 'purchases';
const MENU_MEDICAL = 'medical';

const MENUS = [
    { id: MENU_CHECKINS, name: 'Check-Ins', Icon: Stethoscope },
    { id: MENU_PURCHASES, name: 'Purchases', Icon: ShoppingCart },
    { id: MENU_MEDICAL, name: 'Doctors', Icon: Hospital },
];

// Maps the activeTab navigation state (Doctor Bank's Back button, the shared
// header's menu on other pages) onto a sidebar entry.
const menuFromTab = (tab) => {
    if (tab === MENU_MEDICAL || tab === 'doctors') return MENU_MEDICAL;
    if (tab === MENU_PURCHASES) return MENU_PURCHASES;
    return MENU_CHECKINS;
};

const ITEMS_PER_PAGE = 10;

const SYMPTOM_OPTIONS = [
    { value: 'cough_cold', label: 'Cough + Cold' },
    { value: 'fever', label: 'Fever' },
    { value: 'stomach_ache', label: 'Stomach ache' },
    { value: 'headache', label: 'Headache' },
    { value: 'injury', label: 'Injury' },
    { value: 'other', label: 'Other' },
];
const SYMPTOM_LABEL = SYMPTOM_OPTIONS.reduce((acc, s) => ({ ...acc, [s.value]: s.label }), {});

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'under_treatment', label: 'Under Treatment' },
    { value: 'closed', label: 'Closed' },
];
const STATUS_LABEL = { open: 'Open', under_treatment: 'Under Treatment', closed: 'Closed' };
const getStatusTone = (status) => (status === 'closed' ? 'closed' : status === 'under_treatment' ? 'under-treatment' : 'open');

// healthStatus values stored on the check-in (see backend medicalCheckIns model).
const SEVERITY = {
    important: { label: 'Important', tone: 'important', Icon: AlertCircle },
    critical: { label: 'Urgent', tone: 'urgent', Icon: Siren },
    normal: { label: 'Normal', tone: 'normal', Icon: null },
};

// Local-date key (YYYY-MM-DD) — avoids toISOString's UTC shift in IST.
const toDateKey = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const currentMonthRange = () => {
    const now = new Date();
    return {
        start: toDateKey(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: toDateKey(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
};

const listOf = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const latest = (list) => (list.length > 0 ? list[list.length - 1] : null);

const getStudentName = (item) => item?.userName || item?.studentId?.name || 'N/A';
const getStudentKey = (item) => item?.studentId?._id || item?.studentId || item?.userName || item?._id;

const getSymptomsText = (item) => {
    const labels = listOf(item.symptoms).filter((s) => s !== 'other').map((s) => SYMPTOM_LABEL[s] || s);
    if (item.customSymptom) labels.push(item.customSymptom);
    return labels.length > 0 ? labels.join(', ') : '-';
};

const getTestsText = (item) => latest(listOf(item.doctorVisits))?.testDetails || '-';

const getDrVisitText = (item) => {
    const visit = latest(listOf(item.doctorVisits));
    if (!visit) return '-';
    return [visit.doctorName, visit.hospitalName].filter(Boolean).join(' / ') || '-';
};

const getFollowUpText = (item) => {
    const followUp = latest(listOf(item.followUps));
    if (!followUp) return '-';
    if (followUp.status === 'completed') return 'Completed';
    if (followUp.followUpDate) return formatDate(followUp.followUpDate);
    return '-';
};

const getAmountSpent = (item) => [...listOf(item.doctorVisits), ...listOf(item.followUps)]
    .reduce((sum, entry) => sum + (Number(entry.amountSpent) || 0), 0);

const getItemBalagruhaId = (item) => {
    const raw = listOf(item.balagruhaIds)[0];
    return typeof raw === 'object' ? raw?._id : raw;
};

const dedupeById = (items) => {
    const seen = new Set();
    return items.filter((item) => {
        const key = item?._id;
        if (key === undefined || key === null) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

function MedicalInchargeDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();
    const { hasPermission, isLoading: rbacLoading, permissions } = useRBAC();
    const userName = localStorage.getItem('name') || 'Medical Incharge';

    const [menuSelected, setMenuSelected] = useState(() => menuFromTab(location.state?.activeTab));
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const [balagruhas, setBalagruhas] = useState([]);
    const [users, setUsers] = useState([]);
    const [checkIns, setCheckIns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [purchaseData, setPurchaseData] = useState([]);
    const [isfShopData, setIsfShopData] = useState([]);

    const [dateRange, setDateRange] = useState(currentMonthRange);
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [symptomFilter, setSymptomFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [balagruhaFilter, setBalagruhaFilter] = useState('all');
    const [sortDir, setSortDir] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);

    const [showForm, setShowForm] = useState(false);
    const [editingCheckIn, setEditingCheckIn] = useState(null);
    const [viewingCheckIn, setViewingCheckIn] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const permissionsLoaded = Object.keys(permissions || {}).length > 0;
    const can = (moduleName, actionName = 'Read') => {
        if (rbacLoading || !permissionsLoaded) return false;
        if (actionName.toLowerCase() === 'read') {
            return hasPermission(moduleName, 'Read') || hasPermission(moduleName, 'Manage');
        }
        return hasPermission(moduleName, actionName);
    };

    const balagruhaIds = useMemo(() => balagruhas.map((b) => b._id), [balagruhas]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const fetchBalagruhas = async () => {
        try {
            const response = await getUserBalagruhas();
            const list = Array.isArray(response?.data) ? response.data : [];
            setBalagruhas(list.filter((b) => b._id !== 'STOCK'));
        } catch (error) {
            showToast('Error fetching balagruha', 'error');
            setBalagruhas([]);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await getAssignableUsersForSchedule();
            setUsers(response?.data || []);
        } catch (error) {
            setUsers([]);
        }
    };

    const fetchCheckIns = async () => {
        if (balagruhaIds.length === 0) { setCheckIns([]); return; }
        setLoading(true);
        try {
            const response = await getMedicalConditionBasedOnBalagruha({ balagruhaIds });
            setCheckIns(response?.success ? (response.data?.medicalCheckIns || []) : []);
        } catch (error) {
            showToast('Failed to load medical check-ins', 'error');
            setCheckIns([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchases = async () => {
        if (balagruhaIds.length === 0) { setPurchaseData([]); return; }
        try {
            const responses = await Promise.all(balagruhaIds.map((balagruhaId) => getMyPurchaseRequests({ balagruhaId })));
            setPurchaseData(dedupeById(responses.flatMap((r) => r?.data?.requests || [])));
        } catch (error) {
            setPurchaseData([]);
        }
    };

    const fetchIsfShop = async () => {
        if (balagruhaIds.length === 0) { setIsfShopData([]); return; }
        try {
            const responses = await Promise.all(balagruhaIds.map((balagruhaId) => getCoachDeliveries({ balagruhaId, status: 'all', limit: 100 })));
            setIsfShopData(dedupeById(responses.flatMap((r) => r?.orders || [])));
        } catch (error) {
            setIsfShopData([]);
        }
    };

    // Covers navigating back here while still mounted (useState's initializer only runs once).
    useEffect(() => {
        if (location.state?.activeTab) setMenuSelected(menuFromTab(location.state.activeTab));
    }, [location.state]);

    useEffect(() => {
        fetchBalagruhas();
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchCheckIns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [balagruhaIds]);

    useEffect(() => {
        if (menuSelected !== MENU_PURCHASES) return;
        fetchPurchases();
        fetchIsfShop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuSelected, balagruhaIds]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, symptomFilter, statusFilter, balagruhaFilter, dateRange]);

    const inDateRange = (date) => {
        const key = toDateKey(date);
        if (!key) return !dateRange.start && !dateRange.end;
        if (dateRange.start && key < dateRange.start) return false;
        if (dateRange.end && key > dateRange.end) return false;
        return true;
    };

    const rangeCheckIns = useMemo(
        () => checkIns.filter((item) => inDateRange(item.date)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [checkIns, dateRange]
    );

    const overview = useMemo(() => {
        const todayKey = toDateKey(new Date());
        const isActive = (item) => (item.status || 'open') !== 'closed';
        const appointments = checkIns.reduce((count, item) => {
            const upcomingVisits = listOf(item.doctorVisits).filter((v) => toDateKey(v.visitDate) >= todayKey).length;
            const upcomingFollowUps = listOf(item.followUps)
                .filter((f) => f.status !== 'completed' && toDateKey(f.followUpDate) >= todayKey).length;
            return count + upcomingVisits + upcomingFollowUps;
        }, 0);
        return {
            studentsTreated: new Set(rangeCheckIns.map(getStudentKey)).size,
            important: rangeCheckIns.filter((i) => i.healthStatus === 'important' && isActive(i)).length,
            urgent: rangeCheckIns.filter((i) => i.healthStatus === 'critical' && isActive(i)).length,
            appointments,
            totalSpent: rangeCheckIns.reduce((sum, item) => sum + getAmountSpent(item), 0),
        };
    }, [checkIns, rangeCheckIns]);

    const searchLower = searchText.trim().toLowerCase();
    const filtered = rangeCheckIns.filter((item) => {
        if (balagruhaFilter !== 'all' && String(getItemBalagruhaId(item)) !== String(balagruhaFilter)) return false;
        if (symptomFilter !== 'all' && !listOf(item.symptoms).includes(symptomFilter)) return false;
        if (statusFilter !== 'all' && (item.status || 'open') !== statusFilter) return false;
        if (searchLower) {
            const haystack = `${getStudentName(item)} ${getSymptomsText(item)} ${item.medicines || ''} ${item.notes || ''}`.toLowerCase();
            if (!haystack.includes(searchLower)) return false;
        }
        return true;
    });
    const sorted = [...filtered].sort((a, b) => {
        const diff = new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime();
        return sortDir === 'asc' ? diff : -diff;
    });
    const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));
    const pageSafe = Math.min(currentPage, totalPages);
    const paginated = sorted.slice((pageSafe - 1) * ITEMS_PER_PAGE, pageSafe * ITEMS_PER_PAGE);

    const handleDelete = async (id) => {
        try {
            const response = await deleteMedicalCheckin(id);
            if (response.success) {
                showToast('Check-in deleted', 'success');
                setDeletingId(null);
                fetchCheckIns();
            } else {
                showToast(response.message || 'Failed to delete check-in', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to delete check-in', 'error');
        }
    };

    const rangeLabel = (!dateRange.start && !dateRange.end)
        ? 'All Time'
        : `${formatDate(dateRange.start)}  –  ${formatDate(dateRange.end)}`;

    const overviewCards = [
        {
            key: 'students', tone: 'blue', Icon: Users, title: 'Total Students Treated So Far',
            value: overview.studentsTreated, note: dateRange.start || dateRange.end ? `From ${rangeLabel.replace('  –  ', ' to ')}` : 'All time',
        },
        { key: 'important', tone: 'orange', Icon: AlertCircle, badge: 'Important', title: 'Important Cases', value: overview.important, note: 'Need follow-up and monitoring' },
        { key: 'urgent', tone: 'red', Icon: Siren, badge: 'Urgent', title: 'Urgent Cases', value: overview.urgent, note: 'Need immediate attention' },
        { key: 'appointments', tone: 'green', Icon: CalendarDays, title: 'Appointments', value: overview.appointments, note: 'Upcoming doctor visits' },
        {
            key: 'spent', tone: 'purple', Icon: IndianRupee, title: 'Total Spent',
            value: `₹ ${overview.totalSpent.toLocaleString('en-IN')}`, note: 'On medicines, tests and treatments',
        },
    ];

    const renderCheckIns = () => (
        <div className="mi-checkins medical-tracker">
            <div className="medical-header">
                <div className="medical-title">
                    <span className="medical-title-icon mi-title-icon" aria-hidden="true"><HeartPulse size={26} color="#e0405a" strokeWidth={2.25} /></span>
                    <div>
                        <h3 className="mi-title">Medical Incharge</h3>
                        <div className="medical-subtitle">Healthier Children, Brighter Tomorrows</div>
                    </div>
                </div>
                <div className="medical-date-range">
                    <button type="button" className="medical-date-range-btn mi-date-range-btn" onClick={() => setDateRangeOpen((v) => !v)}>
                        <CalendarDays size={16} /> {rangeLabel} <ChevronDown size={14} />
                    </button>
                    {dateRangeOpen && (
                        <div className="medical-date-range-panel">
                            <label>Start<input type="date" value={dateRange.start} onChange={(e) => setDateRange((r) => ({ ...r, start: e.target.value }))} /></label>
                            <label>End<input type="date" value={dateRange.end} onChange={(e) => setDateRange((r) => ({ ...r, end: e.target.value }))} /></label>
                            <button type="button" onClick={() => setDateRangeOpen(false)}>Apply</button>
                        </div>
                    )}
                </div>
            </div>

            <h4 className="mi-section-title">Overview</h4>
            <div className="mi-overview">
                {overviewCards.map(({ key, tone, Icon, badge, title, value, note }) => (
                    <div key={key} className="mi-stat-card">
                        <div className="mi-stat-top">
                            <span className={`mi-stat-icon tone-${tone}`} aria-hidden="true"><Icon size={24} /></span>
                            {badge && <span className={`mi-stat-badge tone-${tone}`}>{badge}</span>}
                        </div>
                        <div className="mi-stat-title">{title}</div>
                        <div className="mi-stat-value">{value}</div>
                        <div className="mi-stat-note">{note}</div>
                    </div>
                ))}
            </div>

            <h4 className="mi-section-title">Recent Check-ins</h4>
            <div className="medical-toolbar">
                <div className="medical-search">
                    <Search size={16} className="medical-search-icon" aria-hidden="true" />
                    <input type="text" placeholder="Search by name, symptoms, medicine..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={symptomFilter} onChange={(e) => setSymptomFilter(e.target.value)}>
                        <option value="all">All Symptoms</option>
                        {SYMPTOM_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <select className="medical-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Status</option>
                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <div className="medical-filter">
                    <SearchableSelect searchPlaceholder="Search balagruha..." className="medical-filter-select" value={balagruhaFilter} onChange={(e) => setBalagruhaFilter(e.target.value)}>
                        <option value="all">All Balagruhas</option>
                        {balagruhas.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </SearchableSelect>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>
                <button type="button" className="medical-add-btn mi-add-btn" onClick={() => { setEditingCheckIn(null); setShowForm(true); }}>
                    <Plus size={16} /> Record New Check-in
                </button>
            </div>

            <div className="medical-card">
                <div className="medical-table-wrapper">
                    <table className="medical-table">
                        <thead>
                            <tr>
                                <th>Sr. No</th>
                                <th>Name</th>
                                <th className="sortable" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>Date {sortDir === 'asc' ? '↑' : '↓'}</th>
                                <th>Symptoms</th>
                                <th>Medicines</th>
                                <th>Tests</th>
                                <th>Dr. Visit</th>
                                <th>Follow Up</th>
                                <th>Health Severity</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={11} className="medical-empty">Loading...</td></tr>}
                            {!loading && paginated.length === 0 && <tr><td colSpan={11} className="medical-empty">No medical records found</td></tr>}
                            {!loading && paginated.map((item, index) => {
                                const severity = SEVERITY[item.healthStatus] || SEVERITY.normal;
                                const SeverityIcon = severity.Icon;
                                const followUpText = getFollowUpText(item);
                                return (
                                    <tr key={item._id}>
                                        <td>{(pageSafe - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                        <td>{getStudentName(item)}</td>
                                        <td>{formatDate(item.date)}</td>
                                        <td>{getSymptomsText(item)}</td>
                                        <td>{item.medicines || '-'}</td>
                                        <td>{getTestsText(item)}</td>
                                        <td>{getDrVisitText(item)}</td>
                                        <td>
                                            <span className={`medical-chip tone-${followUpText === 'Completed' ? 'closed' : 'open'}`}>{followUpText}</span>
                                        </td>
                                        <td>
                                            <span className={`mi-severity tone-${severity.tone}`}>
                                                {SeverityIcon && <SeverityIcon size={15} />} {severity.label}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`medical-status-pill tone-${getStatusTone(item.status)}`}>{STATUS_LABEL[item.status] || 'Open'}</span>
                                        </td>
                                        <td className="medical-actions-cell">
                                            <button type="button" onClick={() => setViewingCheckIn(item)} aria-label="View" title="View"><Eye size={15} /></button>
                                            <button type="button" onClick={() => { setEditingCheckIn(item); setShowForm(true); }} aria-label="Edit" title="Edit"><Pencil size={14} /></button>
                                            <button type="button" onClick={() => setDeletingId(item._id)} aria-label="Delete" title="Delete" className="danger"><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="medical-footer">
                    <div className="medical-footer-info">
                        {sorted.length === 0
                            ? 'Showing 0 records'
                            : `Showing ${(pageSafe - 1) * ITEMS_PER_PAGE + 1} to ${Math.min(pageSafe * ITEMS_PER_PAGE, sorted.length)} of ${sorted.length} records`}
                    </div>
                    {totalPages > 1 && (
                        <div className="medical-pagination">
                            <button disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>&lsaquo;</button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                                <button key={i} className={pageSafe === i + 1 ? 'active' : ''} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            ))}
                            <button disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>&rsaquo;</button>
                        </div>
                    )}
                </div>
            </div>

            {showForm && (
                <CheckInFormModal
                    initialCheckIn={editingCheckIn}
                    balagruhas={balagruhas}
                    users={users}
                    onClose={() => { setShowForm(false); setEditingCheckIn(null); }}
                    onSaved={fetchCheckIns}
                    sectionVariant="compact"
                />
            )}

            {viewingCheckIn && <ViewCheckInModal checkIn={viewingCheckIn} onClose={() => setViewingCheckIn(null)} />}

            {deletingId && (
                <div className="medical-modal-overlay">
                    <div className="medical-modal medical-confirm-modal">
                        <h3><Stethoscope size={18} /> Delete check-in?</h3>
                        <p>This will permanently remove this medical check-in record.</p>
                        <div className="medical-confirm-actions">
                            <button type="button" className="ghost" onClick={() => setDeletingId(null)}>Cancel</button>
                            <button type="button" className="danger" onClick={() => handleDelete(deletingId)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="coach-dashboard medical-incharge-dashboard w-full">
            <div className="main-content full-width coach-shell">
                <nav className={`coach-sidebar mi-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                    <button
                        type="button"
                        className="coach-sidebar-toggle"
                        onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                        title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    </button>

                    <div className="coach-sidebar-profile">
                        <div className="coach-sidebar-profile-text">
                            <div className="coach-sidebar-name">Hi {userName},</div>
                            {/* Multi-role users get a role dropdown in place of the caption. */}
                            <RoleSwitcher variant="label" fallback={<div className="coach-sidebar-role">(Medical Incharge)</div>} />
                        </div>
                        <div className="coach-avatar">{userName.charAt(0).toUpperCase()}</div>
                    </div>

                    <div className="coach-sidebar-chat-row">
                        <div className="coach-chat-card amma" aria-disabled="true" title="Coming soon">
                            <div className="coach-chat-body">
                                <div className="coach-chat-icon"><MessageCircle size={26} fill="#ffffff" strokeWidth={0} /></div>
                                <div className="coach-chat-label">Amma Chat</div>
                            </div>
                        </div>
                        <div className="coach-chat-card admin" aria-disabled="true" title="Coming soon">
                            <div className="coach-chat-body">
                                <div className="coach-chat-icon"><div className="coach-chat-avatar-icon">A</div></div>
                                <div className="coach-chat-label">Admin Chat</div>
                            </div>
                        </div>
                    </div>

                    <div className="coach-sidebar-menu mi-sidebar-menu">
                        {MENUS.map(({ id, name, Icon }) => (
                            <div key={id} className="coach-sidebar-row">
                                <div
                                    className={`coach-sidebar-item ${menuSelected === id ? 'selected' : ''}`}
                                    onClick={() => setMenuSelected(id)}
                                >
                                    <span className="mi-sidebar-icon" aria-hidden="true"><Icon size={22} strokeWidth={2} /></span>
                                    <span className="coach-sidebar-item-label">{name}</span>
                                </div>
                            </div>
                        ))}
                        <div className="coach-sidebar-row mi-logout-row">
                            <div className="coach-sidebar-item coach-sidebar-item-logout" onClick={handleLogout} title="Logout">
                                <span className="mi-sidebar-icon" aria-hidden="true"><LogOut size={20} strokeWidth={2} /></span>
                                <span className="coach-sidebar-item-label">Logout</span>
                            </div>
                        </div>
                    </div>
                </nav>

                <div className="full-panel mi-panel">
                    {menuSelected === MENU_CHECKINS && renderCheckIns()}
                    {menuSelected === MENU_PURCHASES && (
                        <PurchasePanel
                            balagruhas={balagruhas}
                            purchaseData={purchaseData}
                            isfShopData={isfShopData}
                            canCreate={can('Purchase Management', 'Create')}
                            canManageShop={false}
                            onRequestCreated={fetchPurchases}
                            productApprovalMode="requester"
                        />
                    )}
                    {menuSelected === MENU_MEDICAL && <DoctorBank embedded />}
                </div>
            </div>
        </div>
    );
}

export default MedicalInchargeDashboard;
